import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef
} from '@angular/core'
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs'
import { map, take, takeUntil } from 'rxjs/operators'
import { AttachmentComponent } from '../../../models/entity-configuration'
import { EntityService } from '../../../services/entity.service'

interface EntityReference {
  dtoType: string
  id: number
}

@Component({
  selector: 'jml-lib-tab-container',
  templateUrl: './tab-container.component.html',
  styleUrls: ['./tab-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabContainerComponent implements OnChanges, OnDestroy {
  @Input() tabs: AttachmentComponent[] = []
  @Input() excludedPanels: string[] = []
  @Input() configName: string
  @Input() entryId: number
  @Input() refreshTrigger: Subject<[string, number]>
  @Input() contentTemplate: TemplateRef<any>

  visibleTabs: AttachmentComponent[] = []
  activeIndex: number = 0
  isRefreshing: boolean = false

  private currentTabKey: string = ''

  private selectedTabName: string | null = null
  private destroy$ = new Subject<void>()
  private refreshSubscription: Subscription | null = null
  private loadSubscription: Subscription | null = null

  constructor(
    private entityService: EntityService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Reload when tabs, entryId, or configName changes
    if (changes.tabs || changes.entryId || changes.configName) {
      if (this.entryId && this.configName && this.tabs?.length) {
        this.loadVisibleTabs()

        // Subscribe to refresh on first load
        if (!this.refreshSubscription && this.refreshTrigger) {
          this.subscribeToRefresh()
        }
      }
    }

    // Resubscribe to refresh trigger if it changes
    if (changes.refreshTrigger) {
      this.subscribeToRefresh()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.refreshSubscription?.unsubscribe()
    this.loadSubscription?.unsubscribe()
  }

  private loadVisibleTabs(): void {
    this.loadSubscription?.unsubscribe()

    const entityReference: EntityReference = {
      dtoType: this.configName,
      id: this.entryId
    }

    // Create visibility observables for each tab
    const visibility$ = this.tabs.map((tab) => {
      if (this.excludedPanels?.includes(tab.name)) {
        return of({ tab, visible: false })
      }

      if (!tab.condition) {
        return of({ tab, visible: true })
      }

      return this.evaluateCondition$(tab.condition, entityReference).pipe(
        take(1),
        map((visible) => ({ tab, visible }))
      )
    })

    this.loadSubscription = forkJoin(visibility$)
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        const newVisibleTabs = results.filter((r) => r.visible).map((r) => r.tab)

        const newKey = newVisibleTabs.map((t) => t.name || t.displayName).join('|')
        const tabsChanged = this.currentTabKey !== newKey && this.currentTabKey !== ''
        this.currentTabKey = newKey

        if (tabsChanged) {
          this.isRefreshing = true
          this.cdr.markForCheck()

          setTimeout(() => {
            this.visibleTabs = newVisibleTabs
            this.updateActiveIndex()
            this.isRefreshing = false
            this.cdr.markForCheck()
          }, 0)
        } else {
          this.visibleTabs = newVisibleTabs
          this.updateActiveIndex()
          this.cdr.markForCheck()
        }
      })
  }

  private evaluateCondition$(condition: string, entityReference: EntityReference): Observable<boolean> {
    const expression = condition.includes('${') ? condition : `\${${condition}}`

    return this.entityService.eval(expression, 'boolean', entityReference).pipe(map((response) => !!response.result))
  }

  private updateActiveIndex(): void {
    if (this.selectedTabName && this.visibleTabs.length > 0) {
      const index = this.visibleTabs.findIndex((t) => (t.name || t.displayName) === this.selectedTabName)

      if (index >= 0) {
        this.activeIndex = index
        return
      }
    }

    // Default to first tab
    this.activeIndex = 0
    if (this.visibleTabs.length > 0) {
      this.selectedTabName = this.visibleTabs[0].name || this.visibleTabs[0].displayName
    }
  }

  onTabChange(index: number): void {
    this.activeIndex = index

    const selectedTab = this.visibleTabs[index]
    if (selectedTab) {
      this.selectedTabName = selectedTab.name || selectedTab.displayName
    }
  }

  private subscribeToRefresh(): void {
    this.refreshSubscription?.unsubscribe()

    if (this.refreshTrigger) {
      this.refreshSubscription = this.refreshTrigger.pipe(takeUntil(this.destroy$)).subscribe(([type, id]) => {
        if (type === this.configName && id === this.entryId) {
          this.loadVisibleTabs()
        }
      })
    }
  }

  getTabHeight(tab: AttachmentComponent): string {
    if (tab.height) {
      return tab.height
    }

    if (tab.type === 'dashlet' && tab.dashlet?.type === 'chart') {
      return '300px'
    }

    return ''
  }
}
